import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import { MODAL_ANIMATION, MODAL_Z_INDEX } from './constants';
import { UnifiedModalContext } from './context';
import { ModalContextValue, ModalFullPageProps } from './types';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: ${MODAL_Z_INDEX.modalOverlay};
  background: rgba(0, 0, 0, 0.6);
`;

const FullPageContainer = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: ${MODAL_Z_INDEX.modal};
  background: ${({ theme }) => theme.modal.contentBackgroundColor};
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

export const ModalFullPage: React.FC<ModalFullPageProps> = ({
  open,
  onClose,
  onCancel,
  onConfirm,
  loading = false,
  hideCloseButton = false,
  closeOnEsc = true,
  closeOnOverlayClick = false,
  className,
  children,
}) => {
  const dismiss = useCallback(() => {
    if (loading) return;
    (onCancel ?? onClose)?.();
  }, [onCancel, onClose, loading]);

  useEffect(() => {
    if (!open || !closeOnEsc) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, closeOnEsc, dismiss]);

  const contextValue = useMemo<ModalContextValue>(
    () => ({
      dismiss,
      onConfirm,
      closeOnEsc,
      closeOnOverlayClick,
      loading,
      hideCloseButton,
    }),
    [dismiss, onConfirm, closeOnEsc, closeOnOverlayClick, loading, hideCloseButton],
  );

  return ReactDOM.createPortal(
    <UnifiedModalContext.Provider value={contextValue}>
      <AnimatePresence>
        {open && (
          <>
            <Overlay
              initial={MODAL_ANIMATION.fullpage.initial}
              animate={MODAL_ANIMATION.fullpage.animate}
              exit={MODAL_ANIMATION.fullpage.exit}
              transition={{ duration: MODAL_ANIMATION.duration }}
              onClick={closeOnOverlayClick ? dismiss : undefined}
            />
            <FullPageContainer
              className={className}
              initial={MODAL_ANIMATION.fullpage.initial}
              animate={MODAL_ANIMATION.fullpage.animate}
              exit={MODAL_ANIMATION.fullpage.exit}
              transition={{ duration: MODAL_ANIMATION.duration }}
            >
              {children}
            </FullPageContainer>
          </>
        )}
      </AnimatePresence>
    </UnifiedModalContext.Provider>,
    document.body,
  );
};
